import {
  Menu,
  MenuItem,
  FormControlLabel,
  Divider,
  Grid2,
  IconButton,
  Tooltip,
  Checkbox,
  Collapse,
} from "@mui/material";
import MaterialBox from "../../../../components/MaterialBox";
import MaterialTypography from "../../../../components/MaterialTypography";
import { ExpandMore, FilterAltOff } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useLoading } from "../../../../components/Loading";
import { useSnackbar } from "../../../../context/popup";
import { fetchData } from "../../../../services";
import DropDownConfig from "../../../Utils/DropDownConfig";

const RtoListFilter = ({
  anchorEl,
  open,
  onClose,
  filterData,
  setFilterData,
  onApply,
}) => {
  const sectionHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px 6px",
    background: "#e9ecf5",
    borderRadius: "6px",
    cursor: "pointer",
    mb: 0.5,
  };

  const menuItemStyle = {
    borderRadius: "6px",
    my: 0.2,
    mx: 0.5,
    py: 0.3,
    "&:hover": { backgroundColor: "#dfe6ff" },
  };

  const labelStyle = {
    m: 0,
    "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
  };

  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const [openActive, setOpenActive] = useState(false);
  const [openCity, setOpenCity] = useState(false);
  const [openRtoRegion, setOpenRtoRegion] = useState(false);
  const [openRtoZone, setOpenRtoZone] = useState(false);

  const [cityOptions, setCityOptions] = useState([]);
  const [rtoRegionOptions, setRtoRegionOptions] = useState([]);
  const [rtoZoneOptions, setRtoZoneOptions] = useState([]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      setCircleLoading(true);
      try {
        const [rtoZoneResponse, rtoRegionResponse] = await Promise.all([
          fetchData(
            `/reference/motor/key-value-store?dropdown=true&code=${DropDownConfig?.motorRTOZone}`
          ), 
          fetchData(
            `/reference/motor/key-value-store?dropdown=true&code=${DropDownConfig?.motorRTORegion}`
          ), 
        ]);

        // RTO Zone options
        if (rtoZoneResponse?.success) {
          setRtoZoneOptions(
            rtoZoneResponse.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // RTO Region options
        if (rtoRegionResponse?.success) {
          setRtoRegionOptions(
            rtoRegionResponse.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        showSnackbar("Error Fetching Dropdown", "warning");
      } finally {
        setCircleLoading(false);
      }
    };

    fetchDropdowns();
  }, []);

  const fetchCityDropdown = async () => {
    try {
      const response = await fetchData(
        `/reference/general/city?dropdown=true&isActive=true`
      );
      if (response?.success) {
        setCityOptions(
          response.data?.items?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || []
        );
      }
    } catch (error) {
      console.error("Error fetching city dropdown:", error);
      showSnackbar("Error Fetching City Dropdown", "error");
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClearFilters = () => {
    setFilterData({
      cityId: "",
      rtoRegionId: "",
      rtoZoneId: "",
      isActive: "",
    });
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
      PaperProps={{
        elevation: 3,
        sx: {
          width: 240,
          padding: 1.5,
        },
      }}
    >
      <MaterialBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Title */}
        <MaterialTypography variant="body2" fontWeight="bold">
          Motor RTO Filter
        </MaterialTypography>

        {/* Clear Button */}
        <Tooltip title="Clear Filter" disableInteractive>
          <IconButton
            size="small"
            onClick={onClearFilters}
            sx={{ color: "#ce0707ff" }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
      </MaterialBox>

      {/* Content */}
      <MaterialBox
        ml={1}
        sx={{
          maxHeight: 300,
          overflowY: "auto",
        }}
      >
        <Grid2 container spacing={1}>
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenActive(!openActive)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Active Status
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openActive}>
                <MenuItem
                  disableRipple
                  sx={menuItemStyle}
                  onClick={() =>
                    handleChange({
                      target: { name: "isActive", value: true },
                    })
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filterData.isActive === true}
                      />
                    }
                    label="Active"
                    sx={labelStyle}
                  />
                </MenuItem>

                <MenuItem
                  disableRipple
                  sx={menuItemStyle}
                  onClick={() =>
                    handleChange({
                      target: { name: "isActive", value: false },
                    })
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filterData.isActive === false}
                      />
                    }
                    label="Inactive"
                    sx={labelStyle}
                  />
                </MenuItem>
              </Collapse>
            </MaterialBox>
          </Grid2>
          {/* City */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => {
                  const nextState = !openCity;
                  setOpenCity(nextState);

                  if (nextState && cityOptions.length === 0) {
                    fetchCityDropdown();
                  }
                }}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  City
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openCity ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openCity}>
                {cityOptions?.map((district) => (
                  <MenuItem
                    key={district.value}
                    disableRipple
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: { name: "cityId", value: district.value },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filterData.cityId === district.value}
                        />
                      }
                      label={district.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => {
                  const nextState = !openRtoRegion;
                  setOpenRtoRegion(nextState);

                  // optional: lazy load if needed
                  if (nextState && rtoRegionOptions.length === 0) {
                    // fetchRtoRegionDropdown(); // if you want lazy loading
                  }
                }}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  RTO Region
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openRtoRegion
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openRtoRegion}>
                {rtoRegionOptions?.map((region) => (
                  <MenuItem
                    key={region.value}
                    disableRipple
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: { name: "rtoRegionId", value: region.value },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filterData.rtoRegionId === region.value}
                        />
                      }
                      label={region.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => {
                  const nextState = !openRtoZone;
                  setOpenRtoZone(nextState);

                  // Optional lazy load
                  // if (nextState && rtoZoneOptions.length === 0) {
                  //   fetchRtoZoneDropdown();
                  // }
                }}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  RTO Zone
                </MaterialTypography>

                <IconButton size="small">
                  <ExpandMore
                    sx={{
                      transform: openRtoZone
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </MaterialBox>

              <Collapse in={openRtoZone}>
                {rtoZoneOptions?.map((zone) => (
                  <MenuItem
                    key={zone.value}
                    disableRipple
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: { name: "rtoZoneId", value: zone.value },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filterData.rtoZoneId === zone.value}
                        />
                      }
                      label={zone.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>
        </Grid2>
      </MaterialBox>

      <Divider />

      {/* Apply button */}
      <MenuItem
        onClick={() => onApply()}
        disableRipple
        sx={{
          padding: "6px 16px",
          width: "fit-content",
          minWidth: "0px",
          mx: "auto",
          borderRadius: "6px",
          backgroundColor: "#bcc7e9",
          color: "white",
          display: "flex",
          justifyContent: "center",
          transition: "0.2s ease",

          "&:hover": {
            backgroundColor: "#93a6e8",
          },
        }}
      >
        <MaterialTypography variant="button" fontWeight="bold">
          APPLY FILTER
        </MaterialTypography>
      </MenuItem>
    </Menu>
  );
};

export default RtoListFilter;
